import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Receipt, TrendingUp, Wallet, AlertTriangle, Search, Filter, X,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, CalendarDays,
} from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import { TrendChart } from '@/components/dashboard/DashboardCharts';
import { toast } from '@/components/ui/toast';
import { SelectField } from '@/components/ui/SelectField';

interface Invoice {
  id: string;
  tenantId: string;
  tenantName: string | null;
  provider: string;
  providerRef: string | null;
  planName: string | null;
  amountCents: number;
  currency: string;
  status: string;
  periodEnd: string | null;
  createdAt: string;
}
interface CurrencyTotal { currency: string; amountCents: number; count: number }
interface Revenue {
  totals: {
    byCurrency: CurrencyTotal[];
    byProvider: { provider: string; count: number }[];
    paidCount: number;
    thisMonth?: CurrencyTotal[];
    failedCount?: number;
    payingOrgs?: number;
  };
  monthlyTrend?: { currency: string; points: { month: string; amountCents: number; count: number }[] }[];
  invoices: Invoice[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
}

function formatAmount(amountCents: number, currency: string): string {
  const amt = (amountCents || 0) / 100;
  const c = (currency || 'usd').toLowerCase();
  const frac = amt % 1 ? 2 : 0;
  if (c === 'inr') return `₹${amt.toLocaleString('en-IN', { minimumFractionDigits: frac, maximumFractionDigits: 2 })}`;
  if (c === 'usd') return `$${amt.toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: 2 })}`;
  return `${amt.toLocaleString(undefined, { minimumFractionDigits: frac, maximumFractionDigits: 2 })} ${c.toUpperCase()}`;
}

const PROVIDER_LABEL: Record<string, string> = { stripe: 'Card (Stripe)', razorpay: 'Razorpay' };
const STATUS_STYLE: Record<string, string> = {
  paid: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-600',
  refunded: 'bg-amber-50 text-amber-700',
  pending: 'bg-slate-100 text-slate-600',
};

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${className}`}>{children}</div>;
}

export function SuperAdminRevenuePage() {
  const [data, setData] = useState<Revenue | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [currency, setCurrency] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [trendCurrency, setTrendCurrency] = useState('');

  // Debounce the search box so typing doesn't fire a request per key.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [debounced, status, provider, currency, pageSize]);

  useEffect(() => {
    setLoading(true);
    const params: any = { page, limit: pageSize };
    if (debounced) params.search = debounced;
    if (status) params.status = status;
    if (provider) params.provider = provider;
    if (currency) params.currency = currency;
    saApi.get('/super-admin/revenue', { params })
      .then(r => setData(r.data))
      .catch((e) => toast('error', e?.response?.data?.message || 'Failed to load revenue'))
      .finally(() => setLoading(false));
  }, [page, pageSize, debounced, status, provider, currency]);

  const totals = data?.totals;
  const invoices = data?.invoices ?? [];
  const pg = data?.pagination ?? { page, limit: pageSize, total: invoices.length, totalPages: 1 };
  const hasFilters = !!(debounced || status || provider || currency);

  // Trend: one currency at a time (never summed across currencies). Default
  // to the currency with the most collected.
  const trends = data?.monthlyTrend ?? [];
  const defaultTrend = useMemo(() => {
    const byCur = [...(totals?.byCurrency ?? [])].sort((a, b) => b.amountCents - a.amountCents);
    return byCur[0]?.currency ?? trends[0]?.currency ?? '';
  }, [totals, trends]);
  const activeTrendCurrency = trendCurrency || defaultTrend;
  const trend = trends.find(t => t.currency === activeTrendCurrency);
  const trendData = (trend?.points ?? []).map(p => ({ month: p.month, count: Math.round(p.amountCents / 100) }));
  const trendTotal = (trend?.points ?? []).reduce((a, p) => a + p.amountCents, 0);

  const currencyOptions = Array.from(new Set([...(totals?.byCurrency ?? []).map(c => c.currency), ...trends.map(t => t.currency)]));
  const thisMonthText = (totals?.thisMonth ?? []).map(c => formatAmount(c.amountCents, c.currency)).join(' · ') || formatAmount(0, defaultTrend || 'usd');
  const collectedText = (totals?.byCurrency ?? []).map(c => formatAmount(c.amountCents, c.currency)).join(' · ') || formatAmount(0, 'usd');

  const kpis = [
    { label: 'Total collected', value: collectedText, sub: `${totals?.paidCount ?? 0} paid invoices`, icon: <Wallet className="h-4 w-4" />, tone: 'bg-indigo-50 text-[#6366F1]' },
    { label: 'This month', value: thisMonthText, sub: `${(totals?.thisMonth ?? []).reduce((a, c) => a + c.count, 0)} payments`, icon: <CalendarDays className="h-4 w-4" />, tone: 'bg-emerald-50 text-emerald-600' },
    { label: 'Paid invoices', value: String(totals?.paidCount ?? 0), sub: (totals?.byProvider ?? []).map(p => `${PROVIDER_LABEL[p.provider] || p.provider}: ${p.count}`).join(' · ') || '—', icon: <Receipt className="h-4 w-4" />, tone: 'bg-sky-50 text-sky-600' },
    { label: 'Failed payments', value: String(totals?.failedCount ?? 0), sub: 'Card declined / not completed', icon: <AlertTriangle className="h-4 w-4" />, tone: 'bg-red-50 text-red-600' },
  ];

  const selectCls = 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]';
  const btn = 'h-8 min-w-8 px-2 rounded-lg text-xs border border-slate-200 bg-white text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-600';
  const start = Math.max(1, Math.min(pg.page - 2, pg.totalPages - 4));
  const pageNums = Array.from({ length: Math.min(5, pg.totalPages) }, (_, i) => start + i);
  const from = pg.total === 0 ? 0 : (pg.page - 1) * pg.limit + 1;
  const to = Math.min(pg.page * pg.limit, pg.total);

  return (
    <SuperAdminLayout title="Revenue">
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Revenue</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {totals?.paidCount ?? 0} paid invoices from {totals?.payingOrgs ?? 0} organizations
          </p>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {kpis.map(k => (
            <Card key={k.label} className="p-4">
              <div className="flex items-start justify-between">
                <p className="text-sm text-slate-500">{k.label}</p>
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${k.tone}`}>{k.icon}</span>
              </div>
              {loading && !data ? (
                <div className="mt-3 h-7 w-28 rounded bg-slate-100 animate-pulse" />
              ) : (
                <p className="mt-2 text-xl font-bold text-slate-900 break-words">{k.value}</p>
              )}
              <p className="mt-1 text-xs text-slate-500">{k.sub}</p>
            </Card>
          ))}
        </div>

        {/* Collection trend */}
        <Card className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Collection trend</h3>
              <p className="text-xs text-slate-500">Paid invoices by month · last 12 months</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#6366F1]">
                <TrendingUp className="h-4 w-4" /> {formatAmount(trendTotal, activeTrendCurrency || 'usd')}
              </span>
              {currencyOptions.length > 1 && (
                <SelectField
                  value={activeTrendCurrency}
                  onChange={e => setTrendCurrency(e.target.value)}
                  className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs uppercase focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                >
                  {currencyOptions.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                </SelectField>
              )}
            </div>
          </div>
          {loading && !data ? (
            <div className="h-48 rounded-lg bg-slate-50 animate-pulse" />
          ) : !trendData.length ? (
            <p className="py-10 text-center text-sm text-slate-400">No payments yet</p>
          ) : (
            <TrendChart
              data={trendData}
              label="Collected by month"
              format={(n) => formatAmount(n * 100, activeTrendCurrency)}
            />
          )}
        </Card>

        {/* Search + filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search organization, plan or payment ref..."
              className="h-10 w-80 max-w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            />
          </div>
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`inline-flex h-10 items-center gap-2 rounded-lg border bg-white px-3 text-sm ${showFilters ? 'border-[#6366F1] text-[#6366F1]' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <Filter className="h-4 w-4" /> Filters
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {hasFilters && (
            <button
              onClick={() => { setSearch(''); setStatus(''); setProvider(''); setCurrency(''); }}
              className="inline-flex h-10 items-center gap-1 px-2 text-sm text-slate-500 hover:text-slate-800"
            >
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </div>

        {showFilters && (
          <Card className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-normal text-slate-600 mb-1 block">Status</label>
              <SelectField value={status} onChange={e => setStatus(e.target.value)} className={selectCls}>
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="pending">Pending</option>
              </SelectField>
            </div>
            <div>
              <label className="text-xs font-normal text-slate-600 mb-1 block">Method</label>
              <SelectField value={provider} onChange={e => setProvider(e.target.value)} className={selectCls}>
                <option value="">All</option>
                <option value="stripe">Card (Stripe)</option>
                <option value="razorpay">Razorpay</option>
              </SelectField>
            </div>
            <div>
              <label className="text-xs font-normal text-slate-600 mb-1 block">Currency</label>
              <SelectField value={currency} onChange={e => setCurrency(e.target.value)} className={selectCls}>
                <option value="">All</option>
                {(currencyOptions.length ? currencyOptions : ['usd', 'inr']).map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
              </SelectField>
            </div>
          </Card>
        )}

        {/* Invoices */}
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
            <Receipt className="h-5 w-5 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">Invoices</h3>
            {loading && data && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            <span className="text-xs text-slate-400 ml-auto">{pg.total} total</span>
          </div>

          {loading && !data ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" /></div>
          ) : invoices.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-12">{hasFilters ? 'No invoices match these filters' : 'No invoices yet.'}</p>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {invoices.map((inv, idx) => (
                  <div key={inv.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm">
                          <span className="text-xs text-slate-400 mr-1.5">{from + idx}.</span>
                          <Link to={`/super-admin/organizations?open=${inv.tenantId}`} className="font-medium text-[#6366F1]">{inv.tenantName || '—'}</Link>
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{formatDateTime(inv.createdAt)} · {PROVIDER_LABEL[inv.provider] || inv.provider}</p>
                        <p className="text-xs text-slate-600 mt-1">{inv.planName || '—'}{inv.periodEnd ? ` · until ${formatDate(inv.periodEnd)}` : ''}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-slate-900">{formatAmount(inv.amountCents, inv.currency)}</p>
                        <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_STYLE[inv.status] || STATUS_STYLE.pending}`}>{inv.status}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#6366F1]">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-white">
                      <th className="px-4 py-3 w-12">S.No</th>
                      <th className="px-4 py-3">Invoice</th>
                      <th className="px-4 py-3">Organization</th>
                      <th className="px-4 py-3">Plan</th>
                      <th className="px-4 py-3 text-right">Amount &amp; Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {invoices.map((inv, idx) => (
                      <tr key={inv.id} className="align-top hover:bg-slate-50/70">
                        <td className="px-4 py-3 text-xs text-slate-400 tabular-nums">{from + idx}</td>
                        <td className="px-4 py-3">
                          <p className="text-slate-800 whitespace-nowrap">{formatDateTime(inv.createdAt)}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{PROVIDER_LABEL[inv.provider] || inv.provider}</p>
                          {inv.providerRef && <p className="text-xs text-slate-400 font-mono mt-0.5 break-all">{inv.providerRef}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <Link to={`/super-admin/organizations?open=${inv.tenantId}`} className="font-medium text-[#6366F1] hover:underline">
                            {inv.tenantName || '—'}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-slate-800">{inv.planName || '—'}</p>
                          {inv.periodEnd && <p className="text-xs text-slate-500 mt-0.5">Until {formatDate(inv.periodEnd)}</p>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-semibold text-slate-900 whitespace-nowrap">{formatAmount(inv.amountCents, inv.currency)}</p>
                          <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_STYLE[inv.status] || STATUS_STYLE.pending}`}>{inv.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paging — same as the Leads / Prospects lists */}
          {pg.total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3">
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span>Showing {from}–{to} of {pg.total}</span>
                <label className="flex items-center gap-1.5">
                  Rows per page
                  <SelectField
                    value={pageSize}
                    onChange={e => setPageSize(Number(e.target.value))}
                    className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                  >
                    {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </SelectField>
                </label>
              </div>
              <div className="flex items-center gap-1">
                <span className="mr-2 text-xs text-slate-500">Page {pg.page} of {pg.totalPages}</span>
                <button className={btn} disabled={pg.page <= 1} onClick={() => setPage(1)}>First</button>
                <button className={btn} disabled={pg.page <= 1} onClick={() => setPage(pg.page - 1)} aria-label="Previous page"><ChevronLeft className="h-4 w-4 mx-auto" /></button>
                {pageNums.map(n => (
                  <button key={n} onClick={() => setPage(n)} className={n === pg.page ? 'h-8 min-w-8 px-2 rounded-lg text-xs bg-[#6366F1] text-white' : btn}>{n}</button>
                ))}
                <button className={btn} disabled={pg.page >= pg.totalPages} onClick={() => setPage(pg.page + 1)} aria-label="Next page"><ChevronRight className="h-4 w-4 mx-auto" /></button>
                <button className={btn} disabled={pg.page >= pg.totalPages} onClick={() => setPage(pg.totalPages)}>Last</button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </SuperAdminLayout>
  );
}
